import { MAP_CITY } from "../city.ts";
/** Russian plural form for `n`: `[one, few, many]`, e.g. 1 точка, 2 точки, 5 точек, 11 точек, 21 точка. */
export function plural(n: number, forms: readonly [string, string, string]): string {
  const abs = Math.abs(Math.round(n));
  const last = abs % 10;
  const tens = abs % 100;
  if (tens >= 11 && tens <= 14) return forms[2];
  if (last === 1) return forms[0];
  if (last >= 2 && last <= 4) return forms[1];
  return forms[2];
}

/** "177 079": thousands separated by a narrow no-break space, independent of the browser locale. */
function formatInteger(value: number): string {
  return String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, "\u202f");
}

function formatCompact(value: number): string {
  for (const [scale, unit] of [
    [1e9, "млрд"],
    [1e6, "млн"],
    [1e3, "тыс."],
  ] as const) {
    if (value >= scale)
      return `${(value / scale).toFixed(1).replace(/\.0$/, "").replace(".", ",")}\u00a0${unit}`;
  }
  return formatInteger(value);
}

/** Street types are abbreviated so endpoint labels do not need grammatical declension. */
function shortStreet(name: string): string {
  return name
    .replace(/улица /g, "ул. ")
    .replace(/проспект /g, "пр. ")
    .replace(/переулок /g, "пер. ");
}

/**
 * All UI copy of apps/map lives here (CLAUDE.md: "Строки UI держи в одном модуле"), so a kk.ts can
 * sit next to it later. Every visible string in `src/` must come from this object.
 */
export const ru = {
  cityTraffic: {
    layer: "Пробки",
    time: "Время прогноза",
    legend: "Прогноз:",
    forecast: (time: string) => `Пробки по прогнозу на ${time}`,
    levels: ["свободно", "медленно", "затор", "стоим"],
  },
  cameras: {
    layer: "Камеры скорости",
    title: "Камеры контроля скорости",
    /** T-67 (D34 п.8): one control that opens "Камера здесь / Камеры нет". */
    report: "Сообщить о камере",
    here: "Камера здесь",
    absent: "Камеры нет",
    send: "Согласен, отправить",
    cancel: "Отмена",
    consent:
      "Отправить точку камеры и направление? Случайный UUID установки передаётся только для проверки: сервер хранит его HMAC, точку, направление и время до 90 дней. Треки и IP в журнал не попадают. Подтверждение — от трёх разных установок за 30 дней.",
    success: "Спасибо, учтём после проверки.",
    sending: "Отправляем…",
    error: "Не удалось отправить сообщение. Попробуйте позже.",
    limited: "Лимит сообщений исчерпан. Попробуйте позже.",
    warning: (speed: number, metres: string) =>
      `Камера через ${metres} · ограничение ${speed}\u00a0км/ч`,
    voice: (speed: number) => `Камера, ограничение ${speed}`,
    about:
      "Камеры взяты из снимка OpenStreetMap. Направление — из тега, иначе по движению на односторонней дороге или оба направления на двусторонней; вне дороги — ближайший участок в пределах 30 м. Три разные установки в радиусе 30 м с направлением ±45° за 30 дней подтверждают камеру с источником «пользователи». Повтор рядом с OSM подтверждает существующую камеру. Сообщение «камеры нет» сохраняется для проверки и не скрывает камеру. Ограничение — из тега камеры, иначе из графа. База может быть неполной: ориентируйтесь на знаки.",
  },
  routeVariants: "Варианты маршрута",
  routeVariant: (index: number, seconds: number, metres: number): string =>
    `${index === 0 ? "Основной маршрут" : `Альтернатива ${index}`}: ${Math.round(seconds / 60)} мин · ${(metres / 1000).toFixed(1).replace(".", ",")} км`,
  /** D34 п.9: the product name in the interface; "Almaty Traffic Lab" stays on /about as the engine. */
  appTitle: "Айналма",
  /** The brand header (docs/tasks/T-65 п.4): the wordmark itself is lower-case contours, this is its text. */
  brand: {
    city: MAP_CITY.names.ru,
  },
  /** The city in the header, the first-visit check by position and the launch request of a missing city. */
  cityChoice: {
    change: (city: string) => `${city} — сменить город`,
    missing: "Моего города нет",
    /** Locative case of the registry's cities; a new city falls back to "в городе N". */
    where: (id: string, name: string) =>
      ({ almaty: "в Алматы", astana: "в Астане", shymkent: "в Шымкенте" })[id] ??
      `в городе ${name}`,
    elsewhereTitle: (where: string) => `Похоже, вы ${where}`,
    elsewhereText: (current: string) => `Сейчас открыта карта города ${current}.`,
    open: "Открыть карту",
    stay: "Остаться",
    unsupportedTitle: "Здесь карты пока нет",
    missingTitle: "Нет вашего города?",
    available: (names: string) =>
      `Извините, Айналма пока работает не во всех городах. Сейчас доступны: ${names}.`,
    requestLead: "Запросите запуск — это поможет выбрать следующий город.",
    cityLabel: "Ваш город",
    cityPlaceholder: "Выберите город",
    consent: "Передадим запрос выбранного города. Геопозиция и история адресов не отправляются.",
    send: "Запросить запуск",
    sending: "Отправляем…",
    sent: (name: string) => `Запрос для города ${name} отправлен. Спасибо!`,
    duplicate: (name: string) => `Запрос для города ${name} уже был отправлен. Спасибо!`,
    sendError: "Не удалось отправить запрос. Повторите позже.",
    catalogLoading: "Загружаем список городов…",
    catalogError: "Не удалось загрузить список городов.",
    retry: "Повторить",
    manualLead: "А пока можно открыть карту вручную:",
    close: "Закрыть",
  },
  /** D18 п.11: attribution is mandatory and sits in the map's attribution control. */
  attribution: "© OpenStreetMap contributors",

  // Header: tabs and links (docs/tasks/T-40 п.4, п.6).
  tabRoute: "Маршрут",
  tabDispatch: "Диспетчер",
  /** The third tab (docs/tasks/T-76 п.2): public transport routes from OpenStreetMap. */
  tabTransit: "Транспорт",
  linkAbout: "Как это устроено",
  linkWeb3d: "3D-симуляция центра",
  linkApi: "API",
  aboutTitle: "Как это устроено",
  aboutBack: "К карте",

  whereTo: "Куда?",
  /** The title of the phone's search screen for point A (docs/tasks/T-66 п.3). */
  whereFrom: "Откуда?",
  myLocation: "Моё местоположение",
  /** A point set by a tap on the map: its name in fields and cards; the coordinates go under it (T-66 п.1). */
  pointOnMap: "Точка на карте",
  locationUnavailable: "Геолокация недоступна: укажите адрес или кликните по карте.",
  destinationLine: (label: string) => `Куда: ${label}`,
  destinationNotChosen: "не выбрана",
  /** One line in the field of the phone's search screen (owner, 19.10.2026: no paragraph, no examples). */
  searchPlaceholder: "Адрес, место или кафе",
  locateMe: "Я здесь",
  chooseOrigin: "Укажите точку А",
  originLine: (label: string) => `От: ${label}`,
  sheetTitle: "Куда поедем?",
  sheetSubtitle: "Найдите адрес или коснитесь карты",
  /** The empty search screen of the phone (D34, amendment of 19.10.2026). */
  search: {
    recent: "Недавние",
    clearRecent: "Очистить",
    clearRecentTitle: "Очистить недавние места",
    categories: "Рядом",
    /** Category queries of T-61: the geocoder answers one category word with the nearest places. */
    categoryList: ["Кафе", "Аптека", "АЗС", "Банк", "Супермаркет"],
    pickOnMap: "Указать на карте",
    pickTitle: (target: "from" | "to") =>
      target === "from" ? "Точка А на карте" : "Точка Б на карте",
    pickHint: "Коснитесь карты в нужном месте",
    kindVenue: "заведение",
    kindBuilding: "здание",
    kindStreet: "улица",
  },
  /** "19,3 км · прибытие 17:58 · без пробок 26 мин": the line under the place in the route card. */
  arrival: (clock: string) => `прибытие ${clock}`,
  minutesUnit: "мин",
  /** The folded block of the departure time and the traffic mode on the phone (D34 п.6). */
  departSummary: (time: string) => `Время отправления: ${time}`,
  departNow: "сейчас",
  developer: "Для разработчиков",
  more: "Шаг за шагом",
  share: "Поделиться ссылкой",
  shareCopied: "Ссылка скопирована",
  shareFailed: "Не удалось скопировать ссылку. Скопируйте адрес из строки браузера.",
  withoutTraffic: "без пробок",
  summarySeparator: " · ",
  back: "Назад",
  cancel: "Отмена",
  toHere: "Сюда",
  fromHere: "Отсюда",
  sheetHandle: "Изменить высоту шторки",
  walkToEntrance: (metres: number) => `пешком до подъезда ${Math.round(metres)} м`,
  turns: {
    straight: "прямо",
    slightRight: "плавно направо",
    slightLeft: "плавно налево",
    right: "направо",
    left: "налево",
    uturn: "разворот",
  },
  turnHint: (distance: string, turn: string, name?: string) =>
    `через ${distance} ${turn} на улицу ${name ?? "без названия"}`,

  // Points (docs/tasks/T-40 п.1).
  markerA: "А",
  markerB: "Б",
  fromLabel: "Откуда",
  toLabel: "Куда",
  addressPlaceholder: "Адрес или клик по карте",
  swap: "Поменять местами",
  presetsLabel: "Примеры",
  presetAirportDostyk: "Аэропорт → Достык 136",
  presetSamalKalkaman: "Самал-2 → Калкаман",
  presetDostykSairan: "Достык 136 → Сайран 5",
  presetAirport: "Аэропорт",
  presetDostyk136: "проспект Достык, 136",
  presetSamal2: "Самал-2",
  presetKalkaman: "микрорайон Калкаман",
  presetSairan5: "микрорайон Сайран, 5",
  noSuggestions: "Ничего не найдено",
  searching: "Ищем…",
  sourceOverture: "Overture",

  // Time of day (docs/tasks/T-40 п.2).
  timeLabel: "Время отправления",
  now: "Сейчас",
  nowAt: (clock: string) => `сейчас · ${clock}`,
  trafficModel: "С учётом модели",
  trafficFree: "Свободный поток",
  departure: {
    change: "Время в пути, изменить время выезда",
    edit: "Выезд",
    title: "Время выезда",
    close: "Закрыть выбор времени выезда",
    today: "Сегодня",
    tomorrow: "Завтра",
    day: "День выезда",
    time: `Время суток в ${({ almaty: "Алматы", astana: "Астане", shymkent: "Шымкенте" })[MAP_CITY.id] ?? MAP_CITY.names.ru}`,
    after15: "+15 мин",
    after30: "+30 мин",
    after60: "+1 ч",
    apply: "Пересчитать",
    night: "ночью прогноз по классам дорог, без модели",
    dailyProfiles: "Прогноз зависит от времени суток; модель одинакова сегодня и завтра.",
    at: (clock: string, tomorrow: boolean) => `выезд ${tomorrow ? "завтра " : ""}в ${clock}`,
    arrivalTomorrow: (clock: string) => `прибытие завтра ${clock}`,
    arrivalDayAfter: (clock: string) => `прибытие послезавтра ${clock}`,
    navigationTitle: "Выезжаете сейчас?",
    navigationForecast: (time: string) =>
      `Этот прогноз рассчитан на ${time}. Перед поездкой можно обновить его на сейчас.`,
    recalculateNow: "Пересчитать на сейчас",
    keepForecast: "Поехать с этим прогнозом",
  },

  hintPickA: "Кликните по карте, чтобы поставить точку А, или введите адрес.",
  hintPickB: "Теперь кликните, чтобы поставить точку Б, или введите адрес.",
  hintAgain: "Клик по карте начнёт новый маршрут.",
  routing: "Строим маршрут…",
  reset: "Сбросить",

  // Route panel (docs/tasks/T-40 п.3).
  durationLabel: "По модели",
  freeFlowLabel: "Свободный поток",
  distanceLabel: "Расстояние",
  departLabel: "Отправление",
  sourcesLabel: "Источники скоростей",
  sourceModel: "модель",
  sourceHeuristic: "эвристика",
  sourceObserved: "наблюдения",
  legendTitle: "Цвет линии",
  legendFree: "свободный поток",
  legendTail: "последние метры по проезду",
  congestionForecast: "пробки по прогнозу:",
  congestionLevels: ["свободно", "медленно", "затор", "стоим"],
  legendBySource: "Цвет линии — уровень затора; источник скорости — в долях выше.",
  stepsLabel: "По улицам",
  unnamedStreet: "без названия",
  warningsLabel: "Предупреждения",
  pickupLabel: "Подача",
  dropoffLabel: "Высадка",
  walk: (metres: number) => `пешком ${Math.round(metres)} м`,
  barrierTitle: "за шлагбаумом",
  snapEntrance: "подъезд",
  snapBuilding: "здание",
  snapRoad: "дорога",
  showJson: "Показать JSON ответа",
  hideJson: "Скрыть JSON ответа",
  apiLabel: "API",
  errorPrefix: "Ошибка",
  apiUnreachable: "Сервис маршрутизации недоступен. Запущен ли apps/api?",

  // Dispatcher (docs/tasks/T-40 п.4).
  dispatchIntroLine1:
    "Ближайшая по прямой машина не всегда приедет первой: между ней и вызовом могут быть односторонние улицы, развязки и пробки.",
  dispatchIntroLine2:
    "Матрица ETA по модели на выбранное время показывает, какую машину отправлять на самом деле.",
  dispatchHintCall: "Кликните по карте, чтобы поставить точку вызова.",
  dispatchComputing: "Считаем матрицу…",
  seedLabel: "Сид",
  reshuffle: "Рассыпать заново",
  carLabel: (index: number) => `№${index + 1}`,
  colCar: "Машина",
  colLine: "По прямой",
  colRoad: "По дороге",
  colEta: "ETA",
  nearestByLine: "ближайшая по прямой",
  nearestByEta: "ближайшая по ETA",
  etaDiffLabel: "Разница ETA",
  etaDiffSame: "одна и та же машина",
  unreachable: "недостижимо",
  callMarker: "Вызов",

  // Trip recording (docs/tasks/T-53 п.1): the button, the first-time consent, the indicator.
  navigation: {
    title: "Навигация",
    start: "Поехали",
    exit: "Выйти из навигации",
    waiting: "Определяем местоположение…",
    arrived: "Вы у конца автомобильного маршрута",
    next: (text: string) => `Затем: ${text}`,
    // T-67: the maneuver card says the distance big and the turn with the street under it; the
    // trip card says the minutes big and "1,4 км · прибытие 17:58 · по модели" under them.
    street: (turn: string, name?: string) => `${turn} на улицу ${name ?? "без названия"}`,
    toEnd: "до конца маршрута",
    /** The status line of the trip card is one line: the short form of `record.noWakeLock`. */
    noWakeLock: "Не блокируйте экран: он может погаснуть",
    etaLine: (distance: string, time: string) => `${distance} · прибытие ${time} · по модели`,
    rerouting: "Перестраиваем маршрут…",
    rerouteFailed: "Не удалось перестроить маршрут. Повторим при восстановлении связи.",
    soundOn: "Звук включён",
    soundOff: "Звук выключен",
    consent: "Отправлять трек в модель пробок",
    /** The visible text of the switch chip; the checkbox keeps the full name above (T-67). */
    consentChip: "Трек в модель пробок",
    privacy: "Что отправляется в модель пробок",
    consentText:
      "Только с вашего согласия: координаты, время, скорость и курс отправляются пачками. Без имени и устройства; точки хранятся в памяти, на устройстве — только флаг согласия.",
  },

  record: {
    start: "Записать поездку",
    again: "Записать ещё",
    stop: "Стоп",
    stopTitle: "Остановить запись поездки",
    close: "Закрыть",
    region: "Запись поездки",
    consentTitle: "Записать поездку?",
    consentText:
      "Пока вы едете, телефон отправляет трек в модель пробок: анонимно, без имени и устройства. Сервис хранит скорости по участкам дорог и время, но не координаты.",
    consentAccept: "Начать запись",
    consentCancel: "Отмена",
    waiting: "Ждём сигнал GPS…",
    recording: "Идёт запись",
    stopped: "Запись остановлена",
    sending: "Отправляем последние точки…",
    /** "записано 12 мин · 5,3 км · 640 точек · 38 линков"; `distance` is already formatted. */
    stats: (minutes: number, distance: string, points: number, links: number) =>
      `записано ${minutes} мин · ${distance} · ${points} ${plural(points, ["точка", "точки", "точек"])} · ${links} ${plural(links, ["линк", "линка", "линков"])}`,
    /** The compact label of the stop button: "Стоп · 12 мин". */
    stopWithTime: (minutes: number) => `Стоп · ${minutes} мин`,
    weakSignal: "Слабый сигнал GPS: точки с погрешностью больше 50 м не записываются.",
    sendFailing: "Нет связи с сервисом: точки отправятся, когда связь появится.",
    noWakeLock:
      "Экран может погаснуть и остановить запись: не блокируйте телефон и держите страницу открытой.",
    denied:
      "Геолокация недоступна: разрешите доступ к местоположению для этого сайта и нажмите «Записать поездку» ещё раз.",
    unavailable: "Запись поездки работает только по HTTPS и в браузере с геолокацией.",
    unsent: (points: number) =>
      `Не удалось отправить последние ${points} ${plural(points, ["точку", "точки", "точек"])}.`,
  },

  // Layers (docs/tasks/T-40 п.5).
  layersTitle: "Слои",
  /** T-67: closes the layers card of the phone. */
  layersDone: "Готово",
  layerDataQuality: "Достоверность данных",
  layerLanes: "Полосы улиц",
  layerLanesHint: "Появляются при приближении, с масштаба 16.",
  layerBuildings: "Здания",
  // Building volumes (T-52).
  layerBuildings3d: "Здания объёмно",
  layerEntrances: "Подъезды",
  // The theme (T-70, D35).
  themeTitle: "Тема",
  themeAuto: "Авто",
  themeDay: "День",
  themeNight: "Ночь",
  themeAutoHint: `Ночь наступает по солнцу над ${({ almaty: "Алматы", astana: "Астаной", shymkent: "Шымкентом" })[MAP_CITY.id] ?? MAP_CITY.names.ru}.`,
  cityGraphMismatch: "Данные маршрута не соответствуют выбранному городу. Обновите страницу.",
  cityLoadError: "Не удалось загрузить данные города. Обновите страницу позже.",
  cityTitle: `Айналма — навигатор по ${({ almaty: "Алматы", astana: "Астане", shymkent: "Шымкенту" })[MAP_CITY.id] ?? MAP_CITY.names.ru}`,
  shymkentPresets: ["Аэропорт — центр", "Вокзал — Мега Планет", "Кайтпас-1 — Shymkent Plaza"],
  shymkentPoints: [
    "Аэропорт Шымкент",
    "Центр Шымкента",
    "Вокзал Шымкент",
    "Мега Планет",
    "Кайтпас-1",
    "Shymkent Plaza",
  ],
  astanaPresets: ["Қабанбай Батыр — Тәуелсіздік", "Тұран — Алаш", "Мәңгілік Ел — окружная"],
  astanaPoints: [
    "Қабанбай Батыр × Ұлы Дала",
    "Тәуелсіздік × Бауыржан Момышұлы",
    "Тұран × Сығанақ",
    "Алаш тас жолы",
    "Мәңгілік Ел × Ұлы Дала",
    "Окружная дорога",
  ],

  // The lane scheme (docs/tasks/T-50): legend, link card.
  lanesLegend: {
    title: "Полосная схема",
    osm: "Число полос из OSM (тег lanes): тёмные полосы, сплошной край",
    assumed: "Допущение: жилые улицы без разметки, крупные — с точечными разделителями",
    assumedQuality: "Допущение по классу дороги: светлые полосы, пунктирный край",
    bus: "Автобусная полоса из OSM",
    pocket: "Левый карман из turn:lanes OSM",
    markings:
      "Стрелки: яркие из turn:lanes OSM, бледные допущение. Зебры: белые — переход есть в OSM, бледные — допущение у светофора. Стоп-линии ставит компилятор, поэтому они бледные.",
  },
  corrections: {
    title: "Уточнить участок",
    entry: "Здесь не так?",
    submit: "Отправить уточнение",
    sending: "Отправляем…",
    quick: "2 + 2 + 1 автобусная",
    countHint: "Обычные полосы — без автобусной и кармана. Выделенную полосу укажите отдельно.",
    lanesTo: (direction: string) => `Обычные полосы ${direction}`,
    less: (label: string) => `Уменьшить: ${label}`,
    more: (label: string) => `Увеличить: ${label}`,
    directions: { forward: "по направлению OSM", backward: "против направления OSM" },
    bus: "Есть выделенная автобусная полоса",
    busDirection: "Направление выделенки",
    side: "Сторона по ходу движения",
    sides: { left: "слева", right: "справа", middle: "посередине" },
    moreDetails: "Карман, стрелки и зебра",
    editPocket: "Уточнить карман",
    pocket: "Левый карман есть",
    length: "Длина кармана, м",
    editTurns: "Уточнить стрелки",
    editCrosswalk: "Уточнить зебру",
    crosswalk: "Зебра есть",
    selectedDirection: (direction: string) =>
      `Эти уточнения относятся к выбранному направлению: ${direction}.`,
    arrowHint:
      "Полосы слева направо, включая выделенку и карман. Снимите все отметки, если стрелок нет.",
    addArrowLane: "Добавить полосу со стрелками",
    removeArrowLane: "Убрать последнюю полосу",
    noArrows: "без стрелок",
    note: "Комментарий о дороге (до 300 символов, без имени, контактов и IP)",
    website: "Ваш сайт",
    preview: "Предпросмотр уточнения",
    attribute: "Что",
    ours: "У нас",
    proposed: "По-вашему",
    attributes: {
      lanes: "Обычные полосы",
      bus_lane: "Автобусная",
      pocket: "Левый карман",
      turns: "Стрелки",
      crosswalk: "Зебра",
    },
    unknown: "неизвестно",
    yes: "есть",
    no: "нет",
    metres: (n: number) => `${n} м`,
    unknownHint:
      "? — в карточке нет данных о встречных полосах. Зебра также неизвестна. Значения «у нас» могут быть допущениями компилятора.",
    laneOrder: (first: string, second: string) => `Порядок чисел: ${first} + ${second}.`,
    consent:
      "Согласен отправить уточнение для проверки и правки OSM. Оно хранится до разбора; случайный UUID установки — только в виде солёного хеша. Имя, e-mail и IP не сохраняются.",
    success: (id: string) =>
      `Спасибо! Уточнение № ${id} принято. Подтверждённое уточнение попадёт в OSM после проверки, затем — в следующий экстракт карты.`,
    done: "Готово",
    cancel: "Назад к участку",
    error: "Не удалось отправить. Уточнение осталось в форме — попробуйте ещё раз.",
    limited: "Лимит — 10 уточнений в час. Попробуйте позже; введённые значения сохранены в форме.",
    invalid:
      "Проверьте число полос, линию OSM и согласие. В комментарии допустимы только сведения о дороге, без контактов и IP.",
    tooFast: "Проверьте предпросмотр и отправьте через несколько секунд.",
    way: "Линия OSM, к которой относится уточнение",
    chooseWay: "Выберите линию",
    partChoice: "Часть улицы, к которой относится уточнение",
    partsHint: "Части перечислены по ходу движения. Выбранная часть выделена на карте оранжевым.",
    partsUnavailable:
      "Положение линий недоступно. Автоматический выбор и подсветка части не работают — выберите номер линии OSM вручную.",
    part: (index: number, total: number) => `Часть ${index} из ${total}`,
    partFrom: (name: string) => `от ${shortStreet(name)}`,
    partTo: (name: string) => `до ${shortStreet(name)}`,
    partRange: (from: number, to: number) => (from === to ? `около ${from} м` : `${from}–${to} м`),
    partLength: (length: number) => (length === 0 ? "Длина меньше 1 м" : `Длина ${length} м`),
    tappedPart: "здесь вы нажали",
    wayNumber: (id: number) => `Way ${id}`,
    unavailable:
      "Для уточнения нужно полное описание участка с линией OSM. Попробуйте открыть участок ещё раз.",
    aboutTitle: "Уточнения пользователей",
    about:
      "Нажмите на улицу, затем «Здесь не так?»: укажите полосы и разметку и отправьте без регистрации. Правки проверяются по спутниковым снимкам и попадают на карту со следующим обновлением данных.",
  },
  linkCard: {
    label: "Участок улицы",
    close: "Закрыть",
    unnamed: "Улица без названия",
    oneWay: "одностороннее движение",
    twoWay: "движение в обе стороны",
    highways: {
      trunk: "магистраль",
      trunk_link: "съезд с магистрали",
      primary: "главная улица",
      primary_link: "съезд с главной улицы",
      secondary: "второстепенная улица",
      secondary_link: "съезд со второстепенной улицы",
      tertiary: "районная улица",
      tertiary_link: "съезд с районной улицы",
      residential: "жилая улица",
      unclassified: "местная улица",
      living_street: "жилая зона",
      service: "проезд",
    } as Record<string, string>,
    compass: [
      "на север",
      "на северо-восток",
      "на восток",
      "на юго-восток",
      "на юг",
      "на юго-запад",
      "на запад",
      "на северо-запад",
    ] as readonly string[],
    direction: (compass: string, to: string | null) =>
      to === null ? `Направление: ${compass}` : `Направление: ${compass}, к «${to}»`,
    lanes: (count: number) => `${count} ${plural(count, ["полоса", "полосы", "полос"])}`,
    fromOsm: "по данным OSM",
    assumedLanes: "допущение: в OSM нет числа полос, взято по классу дороги",
    laneLine: (index: number) => `Полоса ${index + 1}`,
    turns: {
      left: "налево",
      through: "прямо",
      right: "направо",
      uturn: "разворот",
      merge: "слияние",
      diverge: "съезд",
    } as Record<string, string>,
    laneKinds: {
      pocket: "левый карман",
      bus: "автобусная",
      general: "",
    } as Record<string, string>,
    tapped: "вы нажали сюда",
    turnsOsm: "Стрелки по turn:lanes из OSM.",
    turnsAssumed: "Стрелки: допущение компилятора, в OSM нет turn:lanes.",
    pocket: (metres: number) => `Левый карман ${metres} м (turn:lanes из OSM, длина допущение).`,
    noPocket: "Левого кармана нет (он есть только там, где его описывает OSM).",
    bus: "Автобусная выделенная полоса: есть, по данным OSM; часы работы допущение, круглосуточно.",
    noBus: "Автобусной выделенной полосы в данных нет.",
    osmWay: (id: number) => `Открыть way ${id} в OpenStreetMap`,
    osmWays: "Участок построен из линий OSM:",
    partial: "Описание участка недоступно, показано то, что есть в тайлах.",
    loading: "Загружаем описание участка…",
    fromHere: "Отсюда",
    toHere: "Сюда",
  },

  /** The "Транспорт" mode (docs/tasks/T-76): routes and stops from OSM, the live layer when there is a feed. */
  transit: {
    title: "Общественный транспорт",
    /** The row of the phone's "Слои" card that turns the mode on (the phone's way in, D34). */
    layer: "Общественный транспорт",
    back: "К маршрутам",
    searchLabel: "Номер или название маршрута",
    searchPlaceholder: "Номер или название, например 12",
    modesLabel: "Вид транспорта",
    modes: {
      bus: "Автобус",
      trolleybus: "Троллейбус",
      brt: "BRT",
      subway: "Метро",
      minibus: "Маршрутка",
      tram: "Трамвай",
      light_rail: "Легкорельсовый",
    } as Record<string, string>,
    /** Chips of the filter: what one sees on the street, in the plural. */
    modeChips: {
      bus: "Автобусы",
      trolleybus: "Троллейбусы",
      brt: "BRT",
      subway: "Метро",
      minibus: "Маршрутки",
      tram: "Трамваи",
      light_rail: "Легкорельсовый",
    } as Record<string, string>,
    count: (n: number) => `${n} ${plural(n, ["маршрут", "маршрута", "маршрутов"])}`,
    found: (n: number, total: number) =>
      n === total
        ? `${n} ${plural(n, ["маршрут", "маршрута", "маршрутов"])}`
        : `Найдено ${n} из ${total}`,
    listLabel: "Маршруты",
    stopListLabel: "Маршруты через остановку",
    loading: "Загружаем маршруты…",
    error: "Не удалось загрузить маршруты. Обновите страницу.",
    retry: "Повторить",
    empty: "Ничего не нашлось. Попробуйте номер или конечную остановку.",
    noModes: "Выберите хотя бы один вид транспорта.",
    /** «Автобус 12», «Метро 1», «BRT Тимирязева» (a line without a number keeps its name). */
    lineTitle: (mode: string, ref: string | null, name: string | null) =>
      ref === null ? (name ?? mode) : `${mode} ${ref}`,
    unnamedTerminal: "без названия",
    terminals: (from: string, to: string) => `${from} — ${to}`,
    stops: (n: number) => `${n} ${plural(n, ["остановка", "остановки", "остановок"])}`,
    directionsLabel: "Направление",
    direction: (from: string | null, to: string | null, index: number) =>
      from !== null && to !== null ? `${from} → ${to}` : `Вариант ${index + 1}`,
    combined:
      "В OpenStreetMap оба направления этого маршрута записаны одним отношением (старая схема): линия показана целиком, порядок остановок может быть неточным.",
    oneDirection: "В OpenStreetMap есть только одно направление этого маршрута.",
    gaps: (n: number) =>
      `В линии маршрута в OSM ${n} ${plural(n, ["разрыв", "разрыва", "разрывов"])}.`,
    noGeometry: "В OpenStreetMap у этого маршрута нет линии, есть только остановки.",
    noStops: "В OpenStreetMap у этого маршрута не отмечены остановки.",
    qualityWarning: (reason: string) => `Данные OSM могут быть неточными: ${reason}`,
    qualityUnknown: "нужна проверка",
    quality: {
      missing_osm: "Нет данных — номер есть в перечне, но отсутствует в снимке OSM",
      not_official: "номера нет в перечне 2025 года; действующий статус не подтверждён",
      gaps: "разрывы или нарушенный порядок участков линии",
      no_geometry: "нет линии маршрута",
      no_reverse: "обратное направление не подтверждено",
      no_stops: "нет остановок",
      invalid_roles: "неверные роли участников маршрута",
      no_master: "направления не объединены в маршрут OSM",
      legacy_directions: "старая схема OSM, направления и порядок остановок не проверены",
      old_edit: "отношение не редактировалось более двух лет",
      length_outlier: "длины вариантов отличаются более чем вдвое",
      terminals_mismatch: "конечные отличаются от текста перечня",
      metadata_missing: "дата последней правки не проверена",
      snapshot_changed: "отношение OSM изменилось после снимка линий",
      missing_members: "часть участников маршрута отсутствует в нашем снимке",
      export_loss: "часть данных потеряна при сборке карты",
    },
    auditSummary: (present: number, total: number, checked: number, ok: number, date: string) =>
      `Из перечня: ${present} из ${total} номеров есть в OSM. Автоматически проверено ${checked}, без замечаний — ${ok} (${date}). Проверка не подтверждает маршрут на местности.`,
    stopsTitle: "Остановки",
    operator: (name: string) => `Перевозчик: ${name}`,
    source: "Источник: OpenStreetMap",
    sourceSnapshot: (date: string) =>
      `Маршруты и остановки — OpenStreetMap, снимок ${date}. Данные могут быть неполными.`,
    osmRelation: (id: number) => `Открыть отношение ${id} в OpenStreetMap`,
    lineLabel: "Маршрут общественного транспорта",
    stopLabel: "Остановка",
    unnamedStop: "Остановка без названия",
    stopLines: (n: number) =>
      n === 0
        ? "Маршрутов через остановку в данных нет"
        : `${n} ${plural(n, ["маршрут", "маршрута", "маршрутов"])} через остановку`,
    close: "Закрыть",
    live: {
      /** The honest line of production until an operator's feed is connected (docs/tasks/T-76 п.3). */
      pending: "Движение в реальном времени появится после подключения данных оператора",
      operator: (seconds: number) => `Данные оператора, обновлено ${seconds} с назад`,
      test: (seconds: number) => `Тестовые данные, не оператор: обновлено ${seconds} с назад`,
      waiting: "Ждём данные о движении…",
      vehicles: (n: number) => `${n} ${plural(n, ["машина", "машины", "машин"])} на линии`,
      none: "Сейчас на линии нет машин с данными",
    },
  },

  landmarks: {
    label: "3D-ориентир",
    hint: "Вращайте и приближайте карту",
    attribution: "3D-модели ориентиров: реконструкции Айналма по OSM и фото, ODbL для геоданных",
    lighting:
      "Ночью фонари и свет на дорогах — схема, а не данные: в OpenStreetMap Алматы фонари не отмечены, а признак освещённости улиц в тайлы не попадает. Где нарисована полосная схема, столбы стоят у бордюра крайней полосы или на разделительной при четырёх и более полосах; где её нет — вдоль дороги по ширине её класса.",
    sites: {
      circus: { name: "Казахский государственный цирк", address: "Абая, 50 · Цирк Мұнарасы" },
      esentai: { name: "Esentai", address: "Аль-Фараби, 77/8 · Tower 77/7 · Apartments 77/1–77/3" },
      "kazakhstan-hotel": { name: "Гостиница «Казахстан»", address: "Достык, 52/2" },
      "central-stadium": { name: "Центральный стадион", address: "Сатпаева, 29/3" },
      "republic-palace": { name: "Дворец Республики", address: "Достык, 56" },
      "almaty-akimat": { name: "Акимат города Алматы", address: "Площадь Республики, 4" },
      "abay-opera": { name: "Театр оперы и балета имени Абая", address: "Кабанбай батыра, 110" },
      "ascension-cathedral": { name: "Вознесенский (Зенковский) собор", address: "Гоголя, 40Б" },
      "almaty-towers": { name: "Almaty Towers", address: "Байзакова, 280" },
      "almaty-tv-tower": { name: "Алматинская телебашня", address: "Кок-Тобе" },
      "baluan-sholak": { name: "Дворец спорта им. Балуана Шолака", address: "Абая, 44" },
      "kok-bazar": { name: "Зелёный базар", address: "Жибек Жолы, 53" },
      baiterek: { name: "Монумент «Байтерек»", address: "бульвар Нуржол, 14" },
      akorda: { name: "Резиденция Акорда", address: "левый берег Есиля, восточный конец Нуржола" },
      "khan-shatyr": { name: "Хан Шатыр", address: "Туран, 37" },
      "transport-tower": { name: "Транспорт Тауэр", address: "Кабанбай батыра, 32/1" },
      kazmunaigas: { name: "Штаб-квартира КазМунайГаз", address: "Кабанбай батыра, 19" },
      "house-of-ministries": { name: "Дом министерств", address: "Мәңгілік Ел, 8" },
      "emerald-quarter": { name: "ЖК «Изумрудный квартал»", address: "Кунаева, 8–10" },
      "golden-towers": { name: "Золотые башни", address: "Мәңгілік Ел, 8 и 10" },
      "central-mosque": { name: "Центральная мечеть Алматы", address: "Пушкина, 16" },
      "academy-of-sciences": { name: "Национальная академия наук РК", address: "Шевченко, 28" },
      "astana-opera": { name: "Астана Опера", address: "Кунаева, 1" },
      "peace-pyramid": { name: "Дворец мира и согласия", address: "Тәуелсіздік, 57" },
      "hazrat-sultan": { name: "Мечеть Хазрет Султан", address: "Қошқарбаев, 48" },
      "nur-alem": { name: "Нур Алем", address: "EXPO, Мәңгілік Ел, В1" },
      "abu-dhabi-plaza": { name: "Abu Dhabi Plaza", address: "Сығанақ, 60" },
    },
  },

  // /about (docs/tasks/T-42 п.1): the honest page of D18 п.2, п.9, п.11 and D19.
  about: {
    searchNear:
      "Подсказки ищутся рядом: для поиска рядом с вами в запросе передаются примерные координаты (до ~100 м); сервер их не сохраняет.",
    venues: "Заведения — OpenStreetMap и Overture Maps Places.",
    venuesLicenses:
      "Данные Meta, Microsoft, PinMeTo — CDLA-Permissive-2.0; Foursquare — Apache-2.0; AllThePlaces — CC0-1.0.",
    venuesAttribution: "Overture Maps Foundation — источники и лицензии",
    venuesNotice: "Copyright 2024 Foursquare Labs, Inc. All rights reserved.",
    venuesNoticeLink: "NOTICE Foursquare",
    subtitle: "Навигатор для городов Казахстана на собственной модели дорожного движения",
    whatTitle: "Что это",
    projectNumbers: {
      title: "Проект в цифрах",
      asOf: (date: string) => `На ${date}`,
      compact: formatCompact,
      km: (value: number) => `${formatInteger(value)}\u00a0км`,
      tokensTitle: "Работа моделей",
      tokensLabel: "токенов обработали модели Claude и Codex",
      tokensDetail: "Код, данные и проверку карты ведут ИИ-агенты под руководством автора проекта.",
      agentsTitle: "Агенты",
      agentsLabel: "сессий и подагентов в работе над проектом",
      agentsDetail: (sessions: number, subagents: number, codex: number) =>
        `Сессии Claude Code — ${formatInteger(sessions)}; их подагенты — ${formatInteger(subagents)}; агенты Codex — ${formatInteger(codex)}.`,
      models: (models: string) => `Модели: ${models}.`,
      modelsPending: "Разбивка по моделям появится после локального подсчёта.",
      unknownModel: "модель не указана",
      roadsTitle: "Дороги",
      roadsLabel: "улиц в сети города",
      roadsDetail: (laneKm: number, lanes: number, links: number) =>
        `${formatInteger(laneKm)}\u00a0км полос. Полос — ${formatInteger(lanes)}; направленных участков — ${formatInteger(links)}.`,
      junctionsTitle: "Перекрёстки",
      junctionsLabel: "узлов с тремя и более соседями",
      junctionsDetail: (signals: number, manoeuvres: number, crossings: number, cameras: number) =>
        `Светофорных объектов — ${formatInteger(signals)}; манёвров «полоса → полоса» — ${formatInteger(manoeuvres)}; переходов — ${formatInteger(crossings)}; камер — ${formatInteger(cameras)}.`,
      decksTitle: "Эстакады и тоннели",
      decksKm: (km: number) => `${km.toFixed(1).replace(".", ",")}\u00a0км`,
      decksLabel: "эстакад, съездов и тоннелей в 3D",
      decksDetail: (
        links: number,
        slabs: number,
        boxes: number,
        bridges: number,
        tunnels: number,
      ) =>
        `Участков — ${formatInteger(links)}; плит — ${formatInteger(slabs)}; коробок перекрёстков на плитах — ${formatInteger(boxes)}. Линий в OSM: мосты — ${formatInteger(bridges)}, тоннели — ${formatInteger(tunnels)}.`,
      buildingsTitle: "Здания и адреса",
      buildingsLabel: "зданий в 3D-модели",
      buildingsDetail: (addressed: number, entrances: number, places: number) =>
        `Адресных зданий — ${formatInteger(addressed)}; входов — ${formatInteger(entrances)}; мест — ${formatInteger(places)}.`,
      osmTitle: "OpenStreetMap",
      osmLabel: "объектов в снимке города",
      osmDetail: (date: string) =>
        `Снимок от ${date}. Открытые данные © OpenStreetMap contributors, ODbL.`,
      modelTitle: "Модель города",
      modelLabel: (count: number) =>
        `${plural(count, ["прогон", "прогона", "прогонов"])} симуляции всего города`,
      modelDetail: (links: number, slots: number, minutes: number, seeds: number) =>
        `${formatInteger(slots)} ${plural(slots, ["интервал", "интервала", "интервалов"])} по ${formatInteger(minutes)}\u00a0мин, по ${formatInteger(seeds)} ${plural(seeds, ["независимому прогону", "независимых прогона", "независимых прогонов"])} на каждый. Профили скорости у ${formatInteger(links)} участков.`,
      workTitle: "Работа над проектом",
      workLabel: "карточек задач",
      workDetail: (decisions: number, commits: number, prs: number, days: number, first: string) =>
        `Решений — ${formatInteger(decisions)}; коммитов — ${formatInteger(commits)}; слитых PR — ${formatInteger(prs)}. Дней с первого коммита (${first}) — ${formatInteger(days)}.`,
    },
    whatIs:
      "Маршруты с прогнозом пробок на любое время суток, навигация с подсказками по полосам, поиск адресов, зданий и заведений на казахском, русском и английском, матрица времени для диспетчерских служб. Карта показывает каждую полосу улицы, эстакады и здания в 3D. Алматы и Астана — в браузере, приложения для iOS и Android, API для партнёров.",
    howTitle: "Как это работает",
    how: [
      {
        title: "Модель города",
        text: "Микросимуляция движения по всей улично-дорожной сети: на каждые полчаса с 6:00 до 22:00 — своя картина скоростей.",
      },
      {
        title: "Свой маршрутизатор",
        text: "Полосы и манёвры, запреты поворотов, светофоры, разделённые дороги и выделенки, дворы и шлагбаумы, перекрытия улиц.",
      },
      {
        title: "Живые данные",
        text: "GPS-треки поездок уточняют модель там, где сейчас едут машины.",
      },
      {
        title: "Своя карта",
        text: "Каждая полоса в натуральную ширину, разметка и стрелки, эстакады и здания в 3D, дневной и ночной режимы.",
      },
      {
        title: "Контроль качества",
        text: "ИИ-агенты сверяют перекрёстки со спутниковыми снимками; найденные расхождения исправляются в OpenStreetMap.",
      },
      {
        title: "Камеры скорости",
        text: "Камеры на карте и предупреждения о них в навигации.",
      },
    ],
    intersectionTitle: "Как перекрёсток становится цифровым",
    intersectionWhy:
      "В открытых данных у большинства улиц не указано число полос, а стрелки по полосам есть лишь у нескольких процентов длины даже главных улиц. Поэтому регулируемые перекрёстки сверяются со спутниковыми снимками: на них видно, как устроено движение на самом деле. Так уже проверены 274 перекрёстка центра Алматы.",
    intersectionSteps: [
      {
        image: "/media/intersection/1-satellite.jpg",
        title: "Спутниковый снимок",
        text: "Видно, сколько на самом деле полос, где карманы, стрелки, переходы и выделенки.",
      },
      {
        image: "/media/intersection/2-schema.jpg",
        title: "Схема по данным OpenStreetMap",
        text: "Компилятор собирает полосы из открытых данных и достраивает недостающее. На восточном подходе Толе би вышло 3 полосы и левый карман.",
      },
      {
        image: "/media/intersection/3-check.jpg",
        title: "Сверка ИИ-агентами",
        text: "Сравнитель ищет расхождения схемы и снимка, скептик пытается их опровергнуть, спорное решает судья. Здесь подтвердилось: на подходе 2 полосы, кармана нет.",
      },
      {
        image: "/media/intersection/4-map.jpg",
        title: "Цифровой перекрёсток",
        text: "Модель на карте Айналмы: 2 полосы на подходе, стрелки, зебры, стоп-линии и выделенка в натуральную ширину.",
      },
      {
        image: "/media/intersection/5-map-3d.jpg",
        title: "В 3D",
        text: "По этой модели строятся маршруты и подсказки по полосам.",
      },
      {
        image: "/media/intersection/6-map-night.jpg",
        title: "Ночью",
        text: "Ночной режим карты для навигации в тёмное время суток.",
      },
    ],
    intersectionExample:
      "Пример: улица Толе би × проспект Абылай хана, окно 160 × 160 м, север сверху.",
    dataTitle: "Данные и лицензии",
    dataOsm: (snapshot: string | null) =>
      `Улицы, здания и адреса — OpenStreetMap${snapshot === null ? "" : `, снимок от ${snapshot}`}. Всё, чего нет в OSM, достраивает собственный компилятор карты.`,
    dataLicence:
      "Данные © OpenStreetMap contributors, лицензия ODbL. Производная база (сеть, граф маршрутизации, адреса) выдаётся по запросу под той же лицензией ODbL.",
    lanesTitle: "Что вы видите при приближении",
    lanesWhat:
      "С масштаба 16 дороги подложки заменяются нашей моделью улиц: каждая полоса нарисована в натуральную ширину (3,5 м), с разделителями, осевой линией, левыми карманами, автобусными полосами, стоп-линиями на светофорах, зебрами и стрелками поворотов. Тап по улице открывает карточку участка: название, направление, число полос и откуда оно взято, стрелки, карман, выделенка и ссылка на линию OSM.",
    hostingTitle: "Где хранятся данные",
    hosting: "Сервис, API и данные пользователей размещены в Казахстане.",
    nextTitle: "Что дальше",
    next: [
      "Новые города Казахстана — по заявкам жителей.",
      "Живые GPS-потоки такси, доставки и общественного транспорта поверх модели города.",
    ],
    linkMap: "К карте",
    linkApi: "Документ OpenAPI",
    linkWeb3d: "3D-симуляция центра",
  },

  access: {
    title: "Доступ к API",
    terms:
      "Маршруты, поиск и матрица времени для встраивания в ваши сервисы — по ключу. Атрибуция © OpenStreetMap contributors (ODbL) обязательна. Оставьте заявку — ключ выдаётся после рассмотрения.",
    name: "Имя",
    organization: "Организация (необязательно)",
    email: "E-mail",
    purpose: "Цель использования (10–1000 символов)",
    consent:
      "Согласен на хранение и обработку заявки: данные хранятся до ответа, не дольше 90 дней.",
    submit: "Отправить заявку",
    sending: "Отправляем…",
    success: "Заявка отправлена. Владелец ответит на указанный e-mail.",
    error: "Не удалось отправить заявку. Проверьте соединение и попробуйте ещё раз.",
    limited: "Слишком много заявок. Попробуйте позже.",
    invalid: "Проверьте поля: имя, e-mail, цель от 10 до 1000 символов и согласие обязательны.",
  },

  /** "12 мин 34 с" / "34 с": seconds rounded, no decimals. */
  duration(seconds: number): string {
    const total = Math.round(seconds);
    const minutes = Math.floor(total / 60);
    const rest = total - minutes * 60;
    return minutes > 0 ? `${minutes} мин ${rest} с` : `${rest} с`;
  },
  /** "12 мин": the dispatcher's big number. */
  minutes(seconds: number): string {
    return `${Math.round(seconds / 60)} мин`;
  },
  /** "5,3 км" / "320 м": metres inside, kilometres only in the UI. */
  distance(metres: number): string {
    if (metres >= 1000) return `${(metres / 1000).toFixed(1).replace(".", ",")} км`;
    return `${Math.round(metres)} м`;
  },
  /** "08:30" from minutes since midnight (wraps past 24:00). */
  clock(minutes: number): string {
    const m = ((Math.round(minutes) % 1440) + 1440) % 1440;
    const h = Math.floor(m / 60);
    return `${String(h).padStart(2, "0")}:${String(m - h * 60).padStart(2, "0")}`;
  },
  /** "61 %" from a 0..1 share. */
  percent(share: number): string {
    return `${Math.round(share * 100)} %`;
  },
  /** One decimal place, for coverage kilometres and percentages (already on a 0..100 scale). */
  decimal(value: number): string {
    return value.toFixed(1).replace(".", ",");
  },
  /** "177 079": thousands separated by a narrow no-break space, independent of the browser locale. */
  // `/p`: a point shared from the Айналма app (apps/ios/docs/SHARING.md).
  place: {
    brand: "Айналма",
    pageTitle: "Точка на карте — Айналма",
    kicker: "Точка на карте Айналмы",
    untitled: "Точка на карте",
    openInApp: "Открыть в приложении",
    routeHere: "Маршрут сюда",
    noApp: "Нет приложения?",
    invalidTitle: "Ссылка на точку повреждена",
    invalidText: "В ссылке нет координат точки. Попросите отправить её ещё раз.",
    openMap: "Открыть карту",
  },
  integer(value: number): string {
    return formatInteger(value);
  },
  /** "17.10.2026 14:05" from an ISO timestamp, in Almaty time (UTC+5); anything else is shown as is. */
  dateTime(iso: string): string {
    const ms = Date.parse(iso);
    if (!Number.isFinite(ms)) return iso;
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(
      new Date(ms + 5 * 3_600_000).toISOString(),
    );
    return m === null ? iso : `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
  },
  /** "11.10.2026" from an ISO timestamp or date; anything else is shown as is. */
  date(iso: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m === null ? iso : `${m[3]}.${m[2]}.${m[1]}`;
  },
  /** "43.23580, 76.96004" for a point without an address. */
  coords(point: { lat: number; lon: number }): string {
    return `${point.lat.toFixed(5)}, ${point.lon.toFixed(5)}`;
  },
} as const;
